/**
 * CPS (Configuration Policy Service) policy names as stored internally.
 */
export type CpsPolicy =
  | 'UserFeedbackFormEnabled'
  | 'SurveyFeedbackFormEnabled'
  | 'ConnectedExperiencesEnabled'
  | 'AllowScreenshots'
  | 'AllowEmailCollection'
  | 'AllowLogCollection';

/**
 * CPS policies record. Each policy can be:
 * - `true`: Explicitly enabled
 * - `false`: Explicitly disabled
 * - `null`: Not configured (defaults to enabled behavior for most policies)
 */
export type CpsPolicies = Record<CpsPolicy, boolean | null>;

/**
 * Status of the compliance store data loading.
 */
export type ComplianceStatus = 'idle' | 'pending' | 'fulfilled' | 'rejected';

/**
 * Persisted compliance data stored in localStorage.
 */
export interface CompliancePersistedData {
  /**
   * Normalized CPS policies that are of interest to Viva Engage.
   */
  readonly policies: CpsPolicies;

  /**
   * The unix timestamp of when the policy data should be re-requested.
   */
  readonly checkInTime: number;

  /**
   * The CPS policies request returns a hash that can be used for subsequent requests.
   * It causes the next response to only contain the policy data if it's changed.
   */
  readonly requestHash: string;

  /**
   * Tracks the version of the CPS policy cache for this user. Used for cache-busting.
   */
  readonly cacheVersion: number;
}

/**
 * Full compliance store state including transient data.
 */
export interface ComplianceState extends Partial<CompliancePersistedData> {
  /**
   * Status of the policy data loading.
   */
  readonly status: ComplianceStatus;

  /**
   * Error from the last failed request.
   */
  readonly error: unknown | undefined;

  /**
   * The unix timestamp of when to next try requesting policy data after a failure.
   * This throttles retry attempts to avoid overwhelming the service.
   */
  readonly failedRequestCheckInTime: number | undefined;
}

/**
 * Public actions available on the compliance store.
 */
export interface ComplianceActions {
  /**
   * Initialize compliance policies by fetching from CPS if needed.
   * @param host - The CPS host URL (e.g., https://clients.config.office.net)
   * @param initiator - Optional telemetry initiator identifier
   */
  readonly initialize: (host: string, initiator?: string) => Promise<void>;

  /**
   * Clear all compliance data and reset to initial state.
   */
  readonly clear: () => void;
}

/**
 * Internal actions used by the compliance store implementation.
 * These should not be used by consumers.
 */
export interface ComplianceInternalActions {
  /** @internal Set the store to pending state. */
  readonly setPending: () => void;

  /** @internal Set fulfilled state with policy data. */
  readonly setFulfilled: (data: CompliancePersistedData) => void;

  /** @internal Set rejected state with error. */
  readonly setRejected: (error: unknown, checkInTime: number) => void;
}

/**
 * Complete compliance store type combining state and actions.
 */
export type ComplianceStore = ComplianceState & ComplianceActions & ComplianceInternalActions;

/**
 * Current cache version. Bump this when changing the cache format
 * to force a fresh fetch for all users.
 */
export const currentCpsCacheVersion = 3;

/**
 * localStorage key for persisting compliance data.
 */
export const complianceStorageKey = 'yammer-compliance';

/**
 * Minutes to wait before retrying after a failed request.
 */
export const failedRequestRetryMinutes = 90;
